import { useCallback, useMemo, useRef, useState } from 'react'
import { useAction, useMutation, useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import {
  Upload,
  Image as ImageIcon,
  Film,
  Play,
  Music2,
  Copy,
  Search,
  Check,
  ArrowUpFromLine,
  Sparkles,
  LayoutGrid,
  Trash2,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from '@/hooks/use-toast'
import { Lightbox } from '@/components/ui/lightbox'

type AssetRow = {
  _id: Id<'assets'>
  type: 'image' | 'video' | 'audio'
  source: 'generated' | 'uploaded' | 'extracted'
  crawled?: boolean
  url: string
  name?: string
  folder?: string
  createdAt: number
  metadata?: {
    width?: number
    height?: number
    duration?: number
    mimeType?: string
    fileSize?: number
  }
  generator?: {
    model: string
    prompt?: string
  }
  isNewGenerated?: boolean
  isLibrary?: boolean
  isCurrentProject?: boolean
  workflowTitle?: string
}

type ScopeType = 'project' | 'all_projects'
const SCOPES: { key: ScopeType; label: string }[] = [
  { key: 'project', label: 'This Video' },
  { key: 'all_projects', label: 'All Assets' },
]

function inferAssetType(file: File): AssetRow['type'] | null {
  if (file.type.startsWith('image/')) return 'image'
  if (file.type.startsWith('video/')) return 'video'
  if (file.type.startsWith('audio/')) return 'audio'
  return null
}

function getMediaDuration(file: File, kind: 'audio' | 'video'): Promise<number> {
  return new Promise((resolve, reject) => {
    const el = document.createElement(kind)
    el.preload = 'metadata'
    el.onloadedmetadata = () => {
      URL.revokeObjectURL(el.src)
      resolve(Number.isFinite(el.duration) ? el.duration : 0)
    }
    el.onerror = () => {
      URL.revokeObjectURL(el.src)
      reject(new Error(`Failed to load ${kind} metadata for ${file.name}`))
    }
    el.src = URL.createObjectURL(file)
  })
}

async function measureImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  if (!file.type.startsWith('image/')) return null
  return new Promise((resolve) => {
    const el = new window.Image()
    el.onload = () => {
      resolve({ width: el.naturalWidth, height: el.naturalHeight })
      URL.revokeObjectURL(el.src)
    }
    el.onerror = () => { URL.revokeObjectURL(el.src); resolve(null) }
    el.src = URL.createObjectURL(file)
  })
}

const SUPPORTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp'])

function convertImageToPng(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new window.Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) { URL.revokeObjectURL(img.src); reject(new Error('Canvas not supported')); return }
      ctx.drawImage(img, 0, 0)
      URL.revokeObjectURL(img.src)
      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('Conversion failed')); return }
        resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.png'), { type: 'image/png' }))
      }, 'image/png')
    }
    img.onerror = () => { URL.revokeObjectURL(img.src); reject(new Error(`Cannot decode ${file.name}`)) }
    img.src = URL.createObjectURL(file)
  })
}

function prettyBytes(bytes?: number) {
  if (!bytes || bytes <= 0) return null
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let i = 0
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i++ }
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

function cleanAssetName(url: string, prompt?: string): string {
  if (prompt) {
    const trimmed = prompt.trim()
    return trimmed.length > 40 ? trimmed.slice(0, 37) + '...' : trimmed
  }
  const filename = url.split('/').pop()?.split('?')[0] ?? 'Asset'
  return filename.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).slice(0, 35)
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

const sourceLabel = (s: AssetRow['source']) =>
  s === 'generated' ? 'Generated' : s === 'uploaded' ? 'Uploaded' : 'Extracted'

type FilterType = 'all' | AssetRow['type']

const FILTERS: { key: FilterType; label: string; icon: typeof ImageIcon }[] = [
  { key: 'all', label: 'All', icon: LayoutGrid },
  { key: 'image', label: 'Img', icon: ImageIcon },
  { key: 'video', label: 'Vid', icon: Film },
  { key: 'audio', label: 'Aud', icon: Music2 },
]

interface AssetLibraryPanelProps {
  workflowId: Id<'workflows'>
}

export function AssetLibraryPanel({ workflowId }: AssetLibraryPanelProps) {
  const [scope, setScope] = useState<ScopeType>('project')
  const [filter, setFilter] = useState<FilterType>('all')
  const [query, setQuery] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [importingId, setImportingId] = useState<string | null>(null)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [lightboxType, setLightboxType] = useState<'image' | 'video'>('image')
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const assets = useQuery(api.assets.listForPanel, { workflowId, scope, limit: 250 }) as AssetRow[] | undefined
  const generateUploadUrl = useMutation(api.workflows.generateUploadUrl)
  const getStorageUrl = useMutation(api.workflows.getStorageUrl)
  const createUploadedAsset = useMutation(api.assets.createUploadedAssetForWorkflow)
  const deleteAssetMutation = useMutation(api.assets.deleteAsset)
  const importAsset = useMutation(api.assets.importAssetToCurrentWorkflow)
  const upscaleImageAction = useAction(api.assets.upscaleImage)

  const handleImport = useCallback(async (sourceAssetId: Id<'assets'>) => {
    setImportingId(sourceAssetId)
    try {
      await importAsset({ sourceAssetId, targetWorkflowId: workflowId })
      toast({ title: 'Imported to video' })
    } catch (e) {
      toast({ title: 'Import failed', description: e instanceof Error ? e.message : 'Unknown error' })
    } finally {
      setImportingId(null)
    }
  }, [importAsset, workflowId])

  const counts = useMemo(() => {
    const rows = assets ?? []
    return {
      all: rows.length,
      image: rows.filter(a => a.type === 'image').length,
      video: rows.filter(a => a.type === 'video').length,
      audio: rows.filter(a => a.type === 'audio').length,
    }
  }, [assets])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return (assets ?? []).filter(a => {
      if (filter !== 'all' && a.type !== filter) return false
      if (!q) return true
      return [a.url, a.source, a.generator?.model ?? '', a.generator?.prompt ?? '']
        .join(' ').toLowerCase().includes(q)
    })
  }, [assets, filter, query])

  const handleUploadFiles = async (files: FileList) => {
    const list = Array.from(files)
    if (list.length === 0) return
    setIsUploading(true)
    try {
      for (const rawFile of list) {
        let file = rawFile
        let type = inferAssetType(file)
        if (!type) { toast({ title: 'Unsupported file', description: `Skipping ${file.name}` }); continue }
        if (type === 'image' && !SUPPORTED_IMAGE_TYPES.has(file.type)) {
          try { file = await convertImageToPng(file) } catch { toast({ title: 'Unsupported image', description: `Could not convert ${file.name}` }); continue }
        }
        const maxSize = type === 'image' ? 25e6 : type === 'audio' ? 50e6 : 200e6
        if (file.size > maxSize) { toast({ title: 'File too large', description: `${file.name} is ${prettyBytes(file.size)} (max ${prettyBytes(maxSize)})` }); continue }
        let duration: number | undefined
        if (type === 'audio') duration = await getMediaDuration(file, 'audio')
        else if (type === 'video') duration = await getMediaDuration(file, 'video')
        let imageDims = type === 'image' ? await measureImageDimensions(file) : null
        const uploadUrl = await generateUploadUrl()
        const res = await fetch(uploadUrl, { method: 'POST', body: file, headers: { 'Content-Type': file.type } })
        if (!res.ok) throw new Error(`Upload failed for ${file.name}`)
        const { storageId } = await res.json()
        let url = await getStorageUrl({ storageId })
        if (!url) throw new Error(`Missing storage URL for ${file.name}`)
        if (imageDims) {
          const upscaled = await upscaleImageAction({ imageUrl: url, width: imageDims.width, height: imageDims.height })
          if (upscaled) { url = upscaled.url; imageDims = { width: upscaled.width, height: upscaled.height } }
        }
        await createUploadedAsset({ workflowId, type, url, metadata: { duration, mimeType: file.type, fileSize: file.size, ...(imageDims && { width: imageDims.width, height: imageDims.height }) } })
      }
    } catch (e) {
      toast({ title: 'Upload failed', description: e instanceof Error ? e.message : 'Unknown error' })
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const copyUrl = useCallback(async (id: string, url: string) => {
    try { await navigator.clipboard.writeText(url) } catch { /* fallback */ }
    setCopiedId(id)
    setTimeout(() => setCopiedId(prev => prev === id ? null : prev), 1500)
  }, [])

  const handleDelete = useCallback(async (assetId: Id<'assets'>) => {
    if (confirmDeleteId !== assetId) { setConfirmDeleteId(assetId); return }
    try { await deleteAssetMutation({ assetId }); setConfirmDeleteId(null) }
    catch { toast({ title: 'Delete failed' }) }
  }, [confirmDeleteId, deleteAssetMutation])

  const isLoading = assets === undefined

  return (
    <div className="h-full flex flex-col">
      {/* Scope + search + filter bar */}
      <div className="shrink-0 px-2 py-2 space-y-2 border-b border-border">
        <div className="flex gap-0.5 rounded-md bg-muted p-0.5">
          {SCOPES.map(({ key, label }) => {
            const active = scope === key
            return (
              <button
                key={key}
                onClick={() => setScope(key)}
                className={cn(
                  'flex-1 h-6 rounded text-[10px] font-medium transition-colors',
                  active
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            )
          })}
        </div>
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search..."
            className="w-full h-7 rounded-md border border-border bg-background pl-6 pr-6 text-[11px] text-secondary-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
          {query && (
            <button onClick={() => setQuery('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 flex items-center justify-center">
              <X size={8} />
            </button>
          )}
        </div>
        <div className="flex gap-0.5">
          {FILTERS.map(({ key, label, icon: Icon }) => {
            const count = counts[key]
            const active = filter === key
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                disabled={key !== 'all' && count === 0}
                className={cn(
                  'flex-1 h-6 rounded text-[10px] font-medium inline-flex items-center justify-center gap-0.5 transition-colors',
                  active ? 'bg-muted text-foreground' : count === 0 && key !== 'all' ? 'text-muted-foreground/50' : 'text-muted-foreground hover:bg-muted'
                )}
              >
                <Icon size={10} />
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Asset grid */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-2">
        {isLoading ? (
          <div className="grid grid-cols-2 gap-1.5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-md overflow-hidden bg-card border border-border">
                <div className="aspect-square bg-muted animate-pulse" />
                <div className="px-1.5 py-1 space-y-1">
                  <div className="h-2.5 w-3/4 bg-muted rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-8 text-center">
            {counts.all === 0 ? (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="mx-auto w-full px-4 py-6 rounded-lg border-2 border-dashed border-border hover:border-border/80 bg-card hover:bg-accent transition-all cursor-pointer flex flex-col items-center gap-2"
              >
                <div className="w-8 h-8 rounded-lg bg-foreground text-background flex items-center justify-center">
                  <ArrowUpFromLine size={16} />
                </div>
                <p className="text-[11px] font-medium text-secondary-foreground">Upload files</p>
                <p className="text-[10px] text-muted-foreground">Images, videos, audio</p>
              </button>
            ) : (
              <>
                <Search size={14} className="mx-auto text-muted-foreground/50 mb-1" />
                <p className="text-[11px] text-muted-foreground">No results</p>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-1.5">
            {filtered.map(asset => (
              <CompactAssetCard
                key={asset._id}
                asset={asset}
                scope={scope}
                isCopied={copiedId === asset._id}
                isConfirmingDelete={confirmDeleteId === asset._id}
                isImporting={importingId === asset._id}
                onCopy={() => copyUrl(asset._id, asset.url)}
                onDelete={() => handleDelete(asset._id)}
                onCancelDelete={() => setConfirmDeleteId(null)}
                onImport={() => handleImport(asset._id)}
                onView={() => {
                  setLightboxUrl(asset.url)
                  setLightboxType(asset.type === 'video' ? 'video' : 'image')
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Upload bar */}
      <div className="shrink-0 px-2 py-2 border-t border-border flex items-center gap-1.5">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className={cn(
            'h-7 px-2.5 rounded-md text-[11px] font-medium inline-flex items-center gap-1.5 transition-colors',
            isUploading ? 'bg-muted text-muted-foreground' : 'bg-foreground text-background hover:bg-foreground/90'
          )}
        >
          <Upload size={11} />
          {isUploading ? 'Uploading...' : 'Upload'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*,audio/*"
          multiple
          className="hidden"
          onChange={e => e.target.files && handleUploadFiles(e.target.files)}
        />
        <span className="text-[9px] text-muted-foreground">
          {counts.all > 0 ? `${counts.all} assets` : 'or drag & drop'}
        </span>
      </div>
      <Lightbox url={lightboxUrl} type={lightboxType} onClose={() => setLightboxUrl(null)} />
    </div>
  )
}

function CompactAssetCard({ asset, scope, isCopied, isConfirmingDelete, isImporting, onCopy, onDelete, onCancelDelete, onImport, onView }: {
  asset: AssetRow
  scope: ScopeType
  isCopied: boolean
  isConfirmingDelete: boolean
  isImporting: boolean
  onCopy: () => void
  onDelete: () => void
  onCancelDelete: () => void
  onImport: () => void
  onView: () => void
}) {
  const isImage = asset.type === 'image'
  const isVideo = asset.type === 'video'
  const name = asset.name ?? cleanAssetName(asset.url, asset.generator?.prompt)
  const hasDuration = (isVideo || asset.type === 'audio') && typeof asset.metadata?.duration === 'number'
  const isExternalScope = scope !== 'project' && !asset.isCurrentProject
  const subLabel = scope === 'all_projects' && asset.workflowTitle
    ? asset.workflowTitle
    : asset.isLibrary
      ? 'Library'
      : `${sourceLabel(asset.source)} · ${relativeTime(asset.createdAt)}`

  return (
    <div
      className={cn(
        'group relative rounded-md overflow-hidden bg-card border transition-all',
        'hover:shadow-sm hover:border-border',
        asset.isNewGenerated ? 'border-primary/40' : 'border-border/70'
      )}
      style={{ contentVisibility: 'auto' }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onView}
        onKeyDown={e => { if (e.key === 'Enter') onView() }}
        className="w-full aspect-square bg-muted relative overflow-hidden cursor-pointer"
      >
        {isImage ? (
          <img src={asset.url} alt={name} loading="lazy" className="h-full w-full object-cover" onError={e => { e.currentTarget.style.display = 'none' }} />
        ) : isVideo ? (
          <div className="relative h-full w-full">
            <div className="absolute inset-0 flex items-center justify-center bg-neutral-800">
              <Film size={14} className="text-neutral-500" />
            </div>
            <video
              src={asset.url}
              preload="metadata"
              muted
              playsInline
              className="relative h-full w-full object-cover"
              onError={e => { e.currentTarget.style.display = 'none' }}
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-6 h-6 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                <Play size={10} className="text-white fill-white ml-px" />
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full w-full flex items-center justify-center bg-amber-50">
            <Music2 size={18} className="text-amber-600/60" />
          </div>
        )}
        {hasDuration && (
          <div className="absolute bottom-0.5 right-0.5">
            <span className="text-[8px] font-medium tabular-nums px-1 py-px rounded bg-black/50 text-white/90 backdrop-blur-sm">
              {formatDuration(asset.metadata!.duration!)}
            </span>
          </div>
        )}
        {asset.isNewGenerated && (
          <div className="absolute top-0.5 left-0.5">
            <span className="text-[7px] font-bold uppercase px-1 py-px rounded bg-primary/90 text-white">New</span>
          </div>
        )}
      </div>

      <div className="px-1.5 py-1 flex items-center gap-0.5">
        {isConfirmingDelete ? (
          <div className="flex items-center gap-0.5 w-full">
            <p className="text-[9px] text-destructive font-medium flex-1 truncate">Delete?</p>
            <button onClick={e => { e.stopPropagation(); onDelete() }} className="h-4 px-1 rounded text-[9px] font-medium bg-destructive text-destructive-foreground">Yes</button>
            <button onClick={e => { e.stopPropagation(); onCancelDelete() }} className="h-4 px-1 rounded text-[9px] font-medium bg-muted text-secondary-foreground">No</button>
          </div>
        ) : (
          <>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-medium text-secondary-foreground truncate leading-tight" title={name}>
                {asset.source === 'generated' && <Sparkles size={8} className="inline-block mr-0.5 -mt-px text-amber-500/70" />}
                {name}
              </p>
              <p className="text-[8px] text-muted-foreground truncate">{subLabel}</p>
            </div>
            {isExternalScope ? (
              <button
                onClick={e => { e.stopPropagation(); onImport() }}
                disabled={isImporting}
                title="Import to current video"
                className={cn('shrink-0 w-4 h-4 rounded flex items-center justify-center',
                  isImporting
                    ? 'text-muted-foreground/50'
                    : 'text-muted-foreground/60 hover:text-foreground opacity-0 group-hover:opacity-100')}
              >
                <ArrowUpFromLine size={8} />
              </button>
            ) : (
              <button
                onClick={e => { e.stopPropagation(); onDelete() }}
                className="shrink-0 w-4 h-4 rounded flex items-center justify-center text-muted-foreground/50 hover:text-destructive opacity-0 group-hover:opacity-100"
              >
                <Trash2 size={8} />
              </button>
            )}
            <button
              onClick={e => { e.stopPropagation(); onCopy() }}
              className={cn('shrink-0 w-4 h-4 rounded flex items-center justify-center', isCopied ? 'text-success' : 'text-muted-foreground/50 hover:text-muted-foreground opacity-0 group-hover:opacity-100')}
            >
              {isCopied ? <Check size={8} /> : <Copy size={8} />}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
